import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import type { ViewProps } from 'react-native';

import { theme, VStack } from '@sg/garnish';
import type { PaymentMethodCard } from '@sg/graphql-schema';

import { getCreditCardIcon, PAYMENT_ADD } from '../../constants';
import { PaymentMethodListItem } from './PaymentMethodListItem';

export type PaymentMethodListProps = Readonly<{
  items: readonly PaymentMethodCard[];
  onSelectItem: (account: PaymentMethodCard) => void;
  onSelectAdd: () => void;
}> &
  ViewProps;

export const PaymentMethodList = ({
  items,
  onSelectItem,
  onSelectAdd,
}: PaymentMethodListProps): React.ReactElement => {
  const { formatMessage } = useIntl();

  return (
    <VStack gap={0} hasDivider={true} dividerColor={theme.colors.LIGHT}>
      {items.map((account) => (
        <PaymentMethodListItem
          key={account.id}
          testID={`account.edit-payment.${account.nickname?.split(' ')?.join('-') ?? account.id}`}
          accessibilityLabel={formatMessage(messages.accountPaymentTitle)}
          accessibilityHint={formatMessage(messages.accountPaymentTitle)}
          iconName={getCreditCardIcon(account.cardType)}
          title={account.nickname}
          subtitle={account.description}
          notice={
            account.expirationState === 'EXPIRED'
              ? formatMessage(messages.accountPaymentExpired)
              : account.expirationState === 'NEARING'
                ? formatMessage(messages.accountPaymentExpiring)
                : null
          }
          tag={account.isDefault ? formatMessage(messages.accountPaymentDefault) : null}
          onPress={() => {
            onSelectItem(account);
          }}
        />
      ))}
      <PaymentMethodListItem
        testID="account.edit-payment.new"
        accessibilityLabel={formatMessage(messages.accountPaymentTitle)}
        accessibilityHint={formatMessage(messages.accountPaymentTitle)}
        iconName={getCreditCardIcon(PAYMENT_ADD)}
        title={formatMessage(messages.accountPaymentNew)}
        onPress={onSelectAdd}
      />
    </VStack>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  accountPaymentTitle: {
    defaultMessage: 'Payment methods',
    description: 'Account | Payment | Title',
  },
  accountPaymentExpired: {
    defaultMessage: 'Card expired',
    description: 'Account | Payment | Expired',
  },
  accountPaymentExpiring: {
    defaultMessage: 'Card will expire soon',
    description: 'Account | Payment | Expiring',
  },
  accountPaymentDefault: {
    defaultMessage: 'Default',
    description: 'Account | Payment | Default',
  },
  accountPaymentNew: {
    defaultMessage: 'Add credit or debit card',
    description: 'Account | Payment | New',
  },
});
